import React, { useState } from "react";
import { Button, Modal, Space, Spin, Typography, message } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { downloadUrlApi } from "../../services/common.services";
import { getFileIcon } from "../../helpers/getFileIcon";

const fileLabel = (file) =>
  file?.file_name ||
  file?.name ||
  file?.path?.split("/").pop() ||
  "Uploaded file";

const extOf = (name) =>
  (name || "").split(".").pop()?.toLowerCase() || "";

const IMAGE_EXTS = new Set(["png", "jpg", "jpeg", "svg", "gif", "webp"]);

const isImage = (file) => {
  if (file?.type?.startsWith?.("image/")) return true;
  return IMAGE_EXTS.has(extOf(fileLabel(file)));
};

const isPdf = (file) => {
  if (file?.type === "application/pdf") return true;
  return extOf(fileLabel(file)) === "pdf";
};

const FilePreview = ({ file }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [url, setUrl] = useState(null);

  if (!file?.path) return null;

  const name = fileLabel(file);

  const handleOpen = async () => {
    setOpen(true);
    if (url) return;
    setLoading(true);
    try {
      const res = await downloadUrlApi(
        `path_key=${encodeURIComponent(file.path)}`
      );
      setUrl(res?.data);
    } catch (err) {
      message.error("Unable to load file preview");
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const renderBody = () => {
    if (loading || !url) {
      return (
        <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
          <Spin />
        </div>
      );
    }
    if (isImage(file)) {
      return (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <img
            src={url}
            alt={name}
            style={{ maxWidth: "100%", maxHeight: "75vh", objectFit: "contain" }}
          />
        </div>
      );
    }
    if (isPdf(file)) {
      return (
        <iframe
          src={url}
          title={name}
          style={{ width: "100%", height: "75vh", border: "none" }}
        />
      );
    }
    return (
      <div style={{ textAlign: "center", padding: 24 }}>
        <Typography.Paragraph>
          Preview is not available for this file type.
        </Typography.Paragraph>
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          href={url}
          target="_blank"
          rel="noreferrer"
        >
          Download
        </Button>
      </div>
    );
  };

  return (
    <>
      <Space
        size={8}
        onClick={handleOpen}
        style={{ cursor: "pointer" }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") handleOpen();
        }}
      >
        {getFileIcon(file)}
        <Typography.Link>{name}</Typography.Link>
      </Space>
      <Modal
        title={name}
        open={open}
        onCancel={() => setOpen(false)}
        footer={null}
        width="85%"
        bodyStyle={{ padding: 16 }}
        destroyOnClose
      >
        {renderBody()}
      </Modal>
    </>
  );
};

export default FilePreview;
